import React, { useState, useEffect } from 'react';
import './NetworkStatus.css';
import FancyButton from '../FancyButton';
import '../FancyButton.css';

interface NetworkStatusProps {
  lastUpdated: string | Date | null;
  pollInterval: number;
}

const NetworkStatus: React.FC<NetworkStatusProps> = ({ lastUpdated, pollInterval }) => {
  const [status, setStatus] = useState<'connected' | 'disconnected' | 'connecting'>('connecting');
  const [lastSuccessTime, setLastSuccessTime] = useState<Date | null>(null);
  const [timeoutMs, setTimeoutMs] = useState(0);
  useEffect(() => {
    // If we have a lastUpdated value, convert it to a Date object
    if (lastUpdated) {
      const lastUpdatedDate = typeof lastUpdated === 'string' 
        ? new Date(lastUpdated) 
        : lastUpdated;
      
      // Set the last success time
      setLastSuccessTime(lastUpdatedDate);
      
      // Set status to connected
      setStatus('connected');
      
      // Calculate timeout based on poll interval (3x the interval is more tolerant)
      setTimeoutMs(pollInterval * 5);
    }
  }, [lastUpdated, pollInterval]);

  // Monitor for disconnection
  useEffect(() => {
    if (!lastSuccessTime || !timeoutMs) return;

    const checkConnection = () => {
      const now = new Date();
      const timeSinceLastUpdate = now.getTime() - lastSuccessTime.getTime();
      
      // Only change status if current status doesn't match expected status
      // This prevents unnecessary re-renders
      const newStatus = timeSinceLastUpdate > timeoutMs ? 'disconnected' : 'connected';
      
      if (status !== newStatus) {
        // Only update if we need to change state
        setStatus(newStatus);
      }
    };

    // Check connection status less frequently (every 2 seconds instead of every second)
    const interval = setInterval(checkConnection, 5000);
    return () => clearInterval(interval);
  }, [lastSuccessTime, timeoutMs]);

  return (
    <div className="network-status">
      <FancyButton
        variant={status === 'connected' ? 'green' : status === 'connecting' ? 'yellow' : status === 'disconnected' ? 'red' : 'blue'}
        style={{ minWidth: 120, pointerEvents: 'none', opacity: 1, display: 'flex', alignItems: 'center', gap: 4, background: 'transparent' }}
        disabled
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span className={`status-pulse ${status}`}></span>
          <span className="status-label">{status === 'connected' ? 'ONLINE' : status === 'connecting' ? 'CONNECTING' : status === 'disconnected' ? 'OFFLINE' : ''}</span>
        </span>
      </FancyButton>
    </div>
  );
};

export default NetworkStatus;
